import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Link, Redirect } from 'react-router-dom';
import bindAllOfThis from "src/utils/BindThisHelper";
import Routes from 'src/core/Routes';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';
import { ReduxStoreModel } from "src/redux/ReduxModel";
import { UserState } from "src/redux/UserReducer";

import DataTable, {
    DataTableColumnProps,
    Renderer,
    Filterer,
    DataTableButton,
    DataTableButtonReset
} from 'src/components/common/DataTable';

import TableRequestDTO from 'src/models/datatable/TableRequestDTO';
import TableResponseDTO from 'src/models/datatable/TableResponseDTO';
import StatementDTO from 'src/models/statement/StatementDTO';
import StatementApiController from 'src/api/StatementApiController';
import AccountApiController from 'src/api/AccountApiController';
import { Roles } from 'src/constants/Roles';
import { Button, Tooltip } from 'antd';
import UpdateStatementDTO from 'src/models/statement/UpdateStatementDTO';

interface StatementsProps extends PageProps<{}>, LocalizeContextProps {
    // From Redux
    User: UserState;
}

interface StatementsState {
    loading: boolean;
    tableRecords: StatementDTO[];
    formRedirect: boolean;
}

class Statements extends React.Component<StatementsProps, StatementsState> {
    private dataTableRef: DataTable<StatementDTO>;

    constructor(props: StatementsProps) {
        super(props);
        bindAllOfThis(this, Statements.prototype);

        this.state = {
            tableRecords: [],
            loading: true,
            formRedirect: false,
        };
    }

    private fetchData(requestState: TableRequestDTO, checkEcho: () => boolean, callback: (response: TableResponseDTO<StatementDTO>) => void): void {
        this.setState({ loading: true });

        StatementApiController.getStatementTableData(requestState)
            .then(results => {
                if (!checkEcho()) {
                    return;
                }

                let tableRecords = results.data.results ? results.data.results : [];

                this.setState({
                    tableRecords: tableRecords,
                    loading: false
                });


                callback(results.data);
            });
    }

    private customTableColumns(): DataTableColumnProps<StatementDTO>[] {
        let editLink = (text, record: StatementDTO) => {
            if (record.allowEdit) {
                return Routes.getRoute(Routes.ADMIN_ADD_EDIT_STATEMENT, record.statementId);
            }
            else {
                return "";
            }
        };

        let defaultTooltipProps = {
            mouseLeaveDelay: 0.5,
            mouseEnterDelay: 0.2
        };

        var columns = [
            DataTable.StandardColumns.Text('admin.statements.columnHeader1', 'statementText'),
            {
                columnProps: {
                    title: this.props.translate('admin.statements.columnHeader2'),
                    dataIndex: 'grades',
                    render: (text => { return text.join(); }),
                    sorter: true
                },
                filterType: Filterer.Text
            },
            {
                columnProps: {
                    key: 'edit',
                    align: 'center',
                    width: 35,
                    render: (text, statement, index) => {
                        if (statement.allowEdit) {
                            return (
                                <Tooltip placement="bottom" title={this.props.translate('admin.common.editColumnHeader')} arrowPointAtCenter {...defaultTooltipProps}>
                                    <Link to={Routes.getRoute(Routes.ADMIN_ADD_EDIT_STATEMENT, statement.statementId)}>
                                        <Button shape="circle" icon="edit" />
                                    </Link>
                                </Tooltip>
                            );
                        } else {
                            return (
                                <Tooltip placement="bottom" title={this.props.translate('admin.common.editColumnHeader')} arrowPointAtCenter {...defaultTooltipProps}>
                                    <Button shape="circle" icon="edit" disabled />
                                </Tooltip>
                            );
                        }
                    }
                }
            }
        ];

        var deleteIcon = {
            columnProps: {
                key: 'delete',
                align: 'center',
                width: 35,
                render: (text, statement, index) => {
                    if (statement.allowEdit) {
                        return (
                            <Tooltip placement="bottom" title={this.props.translate('admin.common.deleteColumnHeader')} arrowPointAtCenter {...defaultTooltipProps}>
                                <Button shape="circle" icon="delete" onClick={() => this.softDeleteStatement(statement)} />
                            </Tooltip>
                        );
                    } else {
                        return (
                            <Tooltip placement="bottom" title={this.props.translate('admin.common.deleteColumnHeader')} arrowPointAtCenter {...defaultTooltipProps}>
                                <Button shape="circle" icon="delete" disabled />
                            </Tooltip>
                        );
                    }
                }
            }
        };

        // If the deep copy button is showing, they haven't deep copied the generic statements, so don't let them delete them
        if (!this.showDeepCopyButton()) {
            columns.push(deleteIcon);
        }

        return columns;
    }

    /**
     * Clear Statement's IsActive property
     */
    private softDeleteStatement(statement: StatementDTO) {
        StatementApiController.softDeleteStatement(statement.statementId).then(() => {
            // Refresh the datatable
            this.dataTableRef.refresh();
        });
    }

    private deepCopyGenericStatements(): void {
        StatementApiController.copyKuderGenericIStatements().then(() => {
            // Refresh the datatable
            this.dataTableRef.refresh();
        });
    }

    /**
     * Only show deep copy option if they aren't a kuder admin and they don't have any of their own "I" Statements
     */
    private showDeepCopyButton(): boolean {
        if (this.props.User.roleID === Roles.KuderAdmin.id || this.state.tableRecords.length === 0) {
            return false;
        }

        var tr: StatementDTO;
        for (tr of this.state.tableRecords) {
            if (!tr.isKuderGeneric) {
                return false;
            }
        }

        return true;
    }

    getButtons() {
        var buttons = [
            new DataTableButton('admin.statements.addButton', () => { this.setState({ formRedirect: true }); }),
            new DataTableButtonReset()
        ];

        if (this.showDeepCopyButton()) {
            buttons.splice(0, 0, new DataTableButton('admin.statements.deepCopyButton', () => { this.deepCopyGenericStatements(); }));
        }

        return buttons;
    }

    render() {
        if (this.state.formRedirect) {
            return <Redirect to={Routes.ADMIN_ADD_EDIT_STATEMENT} push />;
        }

        return (
            <div>
                <h2>{this.props.translate('admin.statements.tableHeader')}</h2>
                <DataTable
                    ref={ref => this.dataTableRef = ref}
                    serverSide={true}
                    tableProps={{ rowKey: 'statementId', loading: this.state.loading }}
                    columns={this.customTableColumns()}
                    data={this.state.tableRecords}
                    fetchData={this.fetchData}
                    buttonBar={this.getButtons()} />
            </div>
        );
    }
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        User: state.User
    };
}

export default connect(mapStateToProps)(withLocalize(Statements));



// WEBPACK FOOTER //
// ./src/pages/admin/Statements.tsx